import { isFunction } from './is';
import { Proto } from './proto';

/**
 * Features:
 * - maxEventListeners - warning when limit exceeded
 * - context - `this` value which should be used for the emitted event
 *
 * TBI (if ever needed):
 * - namespaces
 * - many(name, times, callback, context)
 *
 * @module event-emitter
 */

const DEFAULTS = {
  maxListeners: 10,
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
function Events() {}

Events.prototype = Object.create(null);

/**
 * @param fn {Function}
 * @param ctx {*}
 * @param once {Boolean}
 * @constructor
 */
function Listener(fn, ctx, once) {
  this.fn = fn;
  this.ctx = ctx;
  this.once = Boolean(once);
}

const internal = {
  add(target, name, listener, opts = {}) {
    if (!isFunction(listener)) {
      throw new TypeError('listener must be function');
    }

    if (!target._events[name]) {
      target._events[name] = [];
      target._eventsCount += 1;
    }

    const obj = new Listener(listener, opts.context, opts.once || false);
    if (opts.prepend) {
      target._events[name].unshift(obj);
    } else {
      target._events[name].push(obj);
    }

    // Check for listener leak
    if (
      !target._events[name].warned &&
      target._maxListeners > 0 &&
      target._events[name].length > target._maxListeners
    ) {
      target._events[name].warned = true;
      this.logPossibleMemoryLeak.call(
        target,
        target._events[name].length,
        name,
      );
    }

    return target;
  },

  logPossibleMemoryLeak(count, eventName) {
    let errorMsg = 'Possible EventEmitter memory leak detected.';
    errorMsg += ` ${count} listeners for event "${eventName}" registered.`;

    /* eslint-disable no-console */
    console.error(errorMsg);

    if (console.trace) {
      console.trace();
    }
    /* eslint-enable no-console */
  },
};

/**
 * Class for managing events.
 * Can be extended to provide event functionality in other classes.
 *
 * @param [options] {Object}
 */
function EventEmitter(options = {}) {
  Proto.call(this);

  Object.defineProperties(this, {
    _events: {
      value: new Events(),
      writable: true,
    },
    _eventsCount: {
      value: 0,
      writable: true,
    },
    _maxListeners: {
      value: options.maxListeners,
      writable: true,
    },
  });
}

export default Proto.extend(
  {
    constructor: EventEmitter,

    /**
     * Register listener for the specified event.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @return {EventEmitter}
     */
    on(name, listener, context) {
      return internal.add(this, name, listener, { context });
    },

    /**
     * Alias for ```on(name, listener, context)```.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @chainable
     */
    addListener(name, listener, context) {
      return internal.add(this, name, listener, { context });
    },

    /**
     * Adds the `listener` function to the *beginning* of the listeners array.
     * No checks are made to see if the listener has already been added.
     * Multiple calls passing the same combination of `name` and `listener`
     * will result in the listener being added, and called, multiple times.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @return {EventEmitter}
     * @chainable
     */
    prependListener(name, listener, context) {
      return internal.add(this, name, listener, { context, prepend: true });
    },

    /**
     * Adds a one time `listener` function for the event `name`
     * The next time event is triggered, this listener is removed and then invoked.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @return {EventEmitter}
     */
    once(name, listener, context) {
      return internal.add(this, name, listener, { context, once: true });
    },

    /**
     * Adds the `listener` function to the *beginning* of the listeners array.
     * No checks are made to see if the listener has already been added.
     * Multiple calls passing the same combination of eventName and listener
     * will result in the listener being added, and called, multiple times.
     *
     * @param name
     * @param listener
     * @param context
     * @return {EventEmitter}
     */
    prependOnceListener(name, listener, context) {
      return internal.add(this, name, listener, {
        context,
        prepend: true,
        once: true,
      });
    },

    /**
     * Synchronously calls each of the listeners registered for the event `name`,
     * in the order they were registered, passing the supplied arguments to each.
     *
     * Returns true if the event had listeners, false otherwise.
     *
     * @param {String} name
     * @param {Array<*>} args
     * @return {EventEmitter}
     */
    emit(name, ...args) {
      if (!this._events[name]) {
        return false;
      }

      const callbacks = this._events[name];
      for (let i = 0, end = callbacks.length; i < end; i++) {
        if (callbacks[i].once) {
          this.removeListener(name, callbacks[i].fn, callbacks[i].ctx);
        }
        callbacks[i].fn.apply(callbacks[i].ctx || this, args);
      }

      return true;
    },

    /**
     * Alias for `emit(name, ...args)`.
     *
     * @param {String} name
     * @param {Array<*>} args
     * @return {EventEmitter}
     */
    trigger(name, ...args) {
      return this.emit(name, ...args);
    },

    /**
     * Returns an array listing the events for which the emitter
     * has registered listeners.
     *
     * @return {Array<String>}
     */
    eventNames() {
      return Object.keys(this._events);
    },

    /**
     * Returns the number of listeners listening to the event `name`.
     *
     * @param {String} name
     * @return {Number}
     */
    listenerCount(name) {
      return this.listeners(name).length;
    },

    /**
     * Returns a copy of the array of listeners for the given event `name`.
     *
     * @param {String} name
     * @return {Array<Listener>}
     */
    listeners(name) {
      if (!this._events[name]) {
        return [];
      }

      const listeners = this._events[name];
      const res = new Array(listeners.length);
      for (let i = 0, l = listeners.length; i < l; i++) {
        res[i] = listeners[i];
      }

      return res;
    },

    /**
     * Removes all listeners, or those of the specified `name`.
     *
     * Note that *it is bad practice* to remove listeners added elsewhere in the code,
     * particularly when the EventEmitter instance was created by some other
     * component or module.
     *
     * @param {String} [name]
     * @return {EventEmitter}
     */
    removeAllListeners(name) {
      if (!name) {
        this._events = new Events();
        this._eventsCount = 0;

        return this;
      }

      if (!this._events[name]) {
        return this;
      }

      this._eventsCount -= 1;
      if (this._eventsCount === 0) {
        this._events = new Events();
      } else {
        delete this._events[name];
      }

      return this;
    },

    /**
     * Removes the `listener` with specified `context` from the listener array for the event `name`.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @return {EventEmitter}
     */
    removeListener(name, listener, context) {
      if (!this._events[name]) {
        return this;
      }

      if (!isFunction(listener)) {
        throw new TypeError('listener must be function');
      }

      const listeners = this._events[name];
      const preserve = [];
      // eslint-disable-next-line prefer-destructuring
      for (let i = 0, length = listeners.length; i < length; i++) {
        // collect listeners that don't match
        if (
          listeners[i].fn !== listener ||
          (context && listeners[i].ctx !== context)
        ) {
          preserve.push(listeners[i]);
        }
      }

      if (preserve.length) {
        this._events[name] = preserve;
      } else if (this._eventsCount === 1) {
        this._eventsCount -= 1;
        this._events = new Events();
      } else {
        delete this._events[name];
      }

      return this;
    },

    /**
     * Alias for `removeListener`.
     *
     * @param {String} name
     * @param {Function} listener
     * @param {*} [context]
     * @return {EventEmitter}
     */
    off(name, listener, context) {
      return this.removeListener(name, listener, context);
    },

    /**
     * Returns the current max listener value for the EventEmitter
     * which is either set by emitter.setMaxListeners(n) or defaults
     * to EventEmitter.defaultMaxListeners.
     *
     * @return {Number}
     */
    getMaxListeners() {
      if (this._maxListeners === undefined) {
        return DEFAULTS.maxListeners;
      }

      return this._maxListeners;
    },

    /**
     * By default `EventEmitter` will print a warning if more than 10 listeners
     * are added for a particular event.
     * This is a useful default that helps finding memory leaks.
     * Obviously, not all events should be limited to just 10 listeners.
     * The emitter.setMaxListeners() method allows the limit to be modified
     * for this specific EventEmitter instance.
     * The value can be set to Infinity (or 0) to indicate an unlimited number of listeners.
     *
     * @param {Number} n
     * @return {EventEmitter}
     */
    setMaxListeners(n) {
      if (typeof n !== 'number' || n < 0 || isNaN(n)) {
        throw new TypeError('Argument must be a positive number');
      }

      this._maxListeners = n;

      return this;
    },
  },
  {
    /**
     * @static
     * @return {Number}
     */
    get defaultMaxListeners() {
      return DEFAULTS.maxListeners;
    },

    /**
     * @static
     * @param {Number} n
     */
    set defaultMaxListeners(n) {
      const val = parseInt(n, 10);
      DEFAULTS.maxListeners = val < 0 ? 0 : val;
    },
  },
);
